import type { Block } from "../models.js";
import { recordOf } from "../trackSession.js";
import type { Pick } from "../reading.js";
import type { LineLog, LineReading, PlanDefinition, RecordKind, WeightsReading } from "../types.js";
import { renderItem, renderRows } from "./render.js";
import type { Row } from "./types.js";
import { ROWS_VERSION } from "./valid.js";

/**
 * What a line's exercises log as: one kind for all of them; on an inline round's rendered line, one per
 * exercise in order; or, from `logs` (§32.6), a kind for the line and for members by their place on it.
 */
export type LineRecord = RecordKind | (RecordKind | null)[] | LoggedLine;

export interface LoggedLine {
  all: RecordKind | null;
  members: Map<number, RecordKind>;
}

interface Span {
  start: number;
  end: number;
  /** On an inline round's one line: the member's place among the round's filled members, and how many there are. */
  member?: { at: number; of: number };
}

/** The rendered lines each item writes, by growing prefixes, as the builder's `lineSpans`; null when the spans don't add up to the whole. */
function itemSpans(rows: Row[]): Map<string, Span> | null {
  const spans = new Map<string, Span>();
  let at = 0;
  for (let r = 0; r < rows.length; r++) {
    const row = rows[r]!;
    const end = renderRows(rows.slice(0, r + 1)).length;
    if (row.type === "rounds") {
      const prefix = rows.slice(0, r);
      const header = Math.min(end, renderRows([...prefix, { ...row, items: [] }]).length);
      const ends = row.items.map((_, m) => renderRows([...prefix, { ...row, items: row.items.slice(0, m + 1) }]).length);
      const listed = ends.every((e, m) => e >= (m ? ends[m - 1]! : header) && e <= end) && (ends[ends.length - 1] ?? header) === end;
      if (listed) row.items.forEach((item, m) => spans.set(item.id, { start: m ? ends[m - 1]! : header, end: ends[m]! }));
      else {
        const filled = row.items.filter((item) => renderItem(item).trim() !== "");
        row.items.forEach((item) => {
          const place = filled.indexOf(item);
          spans.set(item.id, place < 0 ? { start: at, end: at } : { start: at, end, member: { at: place, of: filled.length } });
        });
      }
    } else if (row.type === "item") spans.set(row.item.id, { start: at, end });
    at = end;
  }
  return at === renderRows(rows).length ? spans : null;
}

function items(rows: Row[]) {
  return rows.flatMap((row) => (row.type === "item" ? [row.item] : row.type === "rounds" ? row.items : []));
}

/** A line's own exercises: a note line names the exercise above it, which may be another item's. */
function made(line: LineReading | undefined): string[] {
  return line !== undefined && (line.kind === "exercise" || line.kind === "unread") ? line.exerciseIds : [];
}

/** Each item's exercises in a reading of `renderRows(rows)`. */
function itemExercises(rows: Row[], lines: LineReading[]): Map<string, string[]> {
  const spans = itemSpans(rows);
  const out = new Map<string, string[]>();
  if (spans === null) return out;
  const byIndex = new Map(lines.map((line) => [line.index, line]));
  for (const item of items(rows)) {
    const span = spans.get(item.id);
    if (span === undefined) continue;
    const ids: string[] = [];
    for (let i = span.start; i < span.end; i++) for (const id of made(byIndex.get(i))) if (!ids.includes(id)) ids.push(id);
    if (span.member === undefined) out.set(item.id, ids);
    else if (ids.length === span.member.of) out.set(item.id, [ids[span.member.at]!]);
  }
  return out;
}

/** DESIGN §25.6: for each item id, how the phone will log it, from the reading of `renderRows(rows)`. */
export function itemRecords(rows: Row[], reading: WeightsReading): Map<string, RecordKind> {
  const exercises = new Map(reading.plan.blocks.flatMap((b) => b.exercises.map((e) => [e.id, e] as const)));
  const out = new Map<string, RecordKind>();
  for (const [item, ids] of itemExercises(rows, reading.lines)) {
    const exercise = ids.map((id) => exercises.get(id)).find((e) => e !== undefined);
    if (exercise !== undefined) out.set(item, recordOf(exercise));
  }
  return out;
}

/** The reading with `recordOverride` set on the exercises of every item the coach gave a `record`. */
export function applyRecords(reading: WeightsReading, rows: Row[]): WeightsReading {
  const wanted = new Map<string, RecordKind>();
  const exercises = itemExercises(rows, reading.lines);
  for (const item of items(rows)) if (item.record !== undefined) for (const id of exercises.get(item.id) ?? []) wanted.set(id, item.record);
  const plan = {
    ...reading.plan,
    blocks: reading.plan.blocks.map((block) => ({
      ...block,
      exercises: block.exercises.map((e) => {
        const record = wanted.get(e.id);
        // As the phone's `record` setter: whatever only asks "is there a weight" follows the choice.
        return record === undefined ? { ...e } : { ...e, recordOverride: record, isBodyweight: record !== "weightReps" };
      }),
    })),
  };
  return { ...reading, plan, lines: reading.lines.map((line) => ({ ...line, exerciseIds: line.exerciseIds.slice() })) };
}

/** A definition's rows when the form wrote them in this schema and its lines are theirs; else null. */
export function formRows(definition: PlanDefinition): Row[] | null {
  if (definition.writtenWith !== "form" || definition.rowsVersion !== ROWS_VERSION || !Array.isArray(definition.rows)) return null;
  const rendered = renderRows(definition.rows);
  if (rendered.length !== definition.lines.length || rendered.some((line, i) => line !== definition.lines[i])) return null;
  return definition.rows;
}

/** Per rendered line of the rows, what its exercises log as when an item there has a `record`. */
export function lineRecords(rows: Row[]): (LineRecord | null)[] {
  const out: (LineRecord | null)[] = renderRows(rows).map(() => null);
  const spans = itemSpans(rows);
  if (spans === null) return out;
  for (const item of items(rows)) {
    const span = spans.get(item.id);
    if (item.record === undefined || span === undefined) continue;
    for (let i = span.start; i < span.end; i++) {
      if (span.member === undefined) out[i] = item.record;
      else {
        const each = Array.isArray(out[i]) ? (out[i] as (RecordKind | null)[]) : Array.from({ length: span.member.of }, () => null);
        each[span.member.at] = item.record;
        out[i] = each;
      }
    }
  }
  return out;
}

/** Blocks with each line's records set on the exercises it made; `records` is per line of the text read. */
export function blocksWithRecords(blocks: Block[], lines: LineReading[], records: readonly (LineRecord | null)[]): Block[] {
  const wanted = wantedRecords(lines, records);
  if (wanted.size === 0) return blocks;
  return blocks.map((block) => ({
    ...block,
    exercises: block.exercises.map((e) => {
      const record = wanted.get(e.id);
      return record === undefined ? e : { ...e, recordOverride: record, isBodyweight: record !== "weightReps" };
    }),
  }));
}

/** By exercise id, the kind each line's record sets on the exercises it made. */
function wantedRecords(lines: LineReading[], records: readonly (LineRecord | null)[]): Map<string, RecordKind> {
  const wanted = new Map<string, RecordKind>();
  for (const line of lines) {
    const record = records[line.index] ?? null;
    const ids = made(line);
    if (record === null || ids.length === 0) continue;
    if (typeof record === "string") for (const id of ids) wanted.set(id, record);
    else if (Array.isArray(record)) {
      if (record.length === ids.length) record.forEach((r, k) => r !== null && wanted.set(ids[k]!, r));
    } else {
      if (record.all !== null) for (const id of ids) wanted.set(id, record.all);
      for (const [k, r] of record.members) if (k < ids.length) wanted.set(ids[k]!, r);
    }
  }
  return wanted;
}

const RECORD_KINDS = new Set<unknown>(["weightReps", "reps", "time", "done"]);

/**
 * DESIGN §32.6: per line of `lines`, what `logs` set it to log. An entry is for the line with its text:
 * at its `line` when that line still has it, else the nearest line that does and no other entry took,
 * else dropped. Entries not of the shape `validLogs` keeps are skipped, so a stored draft never throws here.
 */
export function logRecords(lines: readonly string[], logs: readonly LineLog[]): (LineRecord | null)[] {
  const out: (LoggedLine | null)[] = lines.map(() => null);
  const valid = (Array.isArray(logs) ? logs : []).filter(
    (log): log is LineLog =>
      typeof log === "object" && log !== null && Number.isInteger(log.line) && log.line >= 0 && typeof log.text === "string" && RECORD_KINDS.has(log.record) &&
      (log.member === undefined || (Number.isInteger(log.member) && log.member >= 0)),
  );
  const placed = new Map<LineLog, number>();
  const taken = new Set<string>();
  const slot = (log: LineLog, at: number) => `${at} ${log.member ?? ""}`;
  for (const log of valid) {
    if (lines[log.line] !== log.text || taken.has(slot(log, log.line))) continue;
    placed.set(log, log.line);
    taken.add(slot(log, log.line));
  }
  for (const log of valid) {
    if (placed.has(log)) continue;
    let best = -1;
    lines.forEach((line, at) => {
      if (line !== log.text || taken.has(slot(log, at))) return;
      if (best < 0 || Math.abs(at - log.line) < Math.abs(best - log.line)) best = at;
    });
    if (best < 0) continue;
    placed.set(log, best);
    taken.add(slot(log, best));
  }
  for (const [log, at] of placed) {
    const line = (out[at] ??= { all: null, members: new Map() });
    if (log.member === undefined) line.all = log.record;
    else line.members.set(log.member, log.record);
  }
  return out;
}

/** The reading of `lines` with `recordOverride` set as `logs` say, as the phone's setter sets it. */
export function applyLogs(reading: WeightsReading, logs: readonly LineLog[]): WeightsReading {
  const wanted = wantedRecords(reading.lines, logRecords(reading.lines.map((line) => line.text), logs));
  const plan = {
    ...reading.plan,
    blocks: reading.plan.blocks.map((block) => ({
      ...block,
      exercises: block.exercises.map((e) => {
        const record = wanted.get(e.id);
        return record === undefined ? { ...e } : { ...e, recordOverride: record, isBodyweight: record !== "weightReps" };
      }),
    })),
  };
  return { ...reading, plan, lines: reading.lines.map((line) => ({ ...line, exerciseIds: line.exerciseIds.slice() })) };
}

/** §61.2: a form definition's `record`s, as picks on the lines its rows render, for its first reading. */
export function formPicks(definition: PlanDefinition): Pick[] {
  const rows = formRows(definition);
  if (rows === null) return [];
  const lines = renderRows(rows);
  return lineRecords(rows).flatMap((record, at): Pick[] => {
    const text = lines[at]!;
    if (record === null) return [];
    if (typeof record === "string") return [{ text, logs: record }];
    if (Array.isArray(record)) return record.flatMap((r, member) => (r === null ? [] : [{ text, member, logs: r }]));
    return [];
  });
}
